import React, {
  FunctionComponent, useEffect, useState,
} from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { SelectionChangedEvent, ColDef } from '@ag-grid-enterprise/all-modules';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import { AppToaster } from 'shared/toaster';
import {
  TargetDeliveryTemplateAccessFunctionPrivileges,
  TargetDeliveryTemplateAccessFunctionRowData,
} from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.types';
import { AuditDetails, WithAuditDetails } from 'data/types';
import * as accessFunctionActions from 'data/accessFunction/accessFunction.actions';
import * as actions from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.actions';
import * as warrantActions from 'xcipio/warrants/warrant-actions';
import * as selectors from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.selectors';
import * as accessFunctionSelectors from 'data/accessFunction/accessFunction.selectors';
import * as templateSelectors from 'data/template/template.selectors';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import {
  AuditActionType,
  AuditService,
  getAuditDetails,
  getAuditFieldInfo,
} from 'shared/userAudit/audit-constants';
import cloneDeep from 'lodash.clonedeep';
import TargetDeliveryTemplateAccessFunctionGrid from './components/TargetDeliveryTemplateAccessFunctionGrid';
import {
  targetDeliveryTemplateAccessFunctionColumnDefs,
} from '../../TargetDeliveryTemplateAccessFunction.types';
import { adaptAccessFunctionsGridRowData } from '../../utils/targetDeliveryTemplateAccessFunction.adapters';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  data: selectors.getTargetDeliveryTemplateAccessFunctionList(state),
  selectedTargetDeliveryTemplate: selectors.getSelectedTargetDeliveryTemplateAccessFunction(state),
  accessFunctions: accessFunctionSelectors.getAccessFunctions(state),
  deliveryFunctionId: state.global.selectedDF,
  privileges: state.authentication.privileges,
  beforeLogout: state.authentication.beforeLogout,
  accessFunctionTemplates: templateSelectors.getEnabledAccessFunctionFormTemplatesByKey(state),
  enabledAccessFunctions: supportedFeaturesSelectors.getEnabledAccessFunction(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
});

const mapDispatch = {
  getAccessFunctions: accessFunctionActions.getAccessFunctionsWithViewOptions,
  goToTargetDeliveryTemplateAccessFunctionCreate: actions.goToTargetDeliveryTemplateAccessFunctionCreate,
  goToTargetDeliveryTemplateAccessFunctionEdit: actions.goToTargetDeliveryTemplateAccessFunctionEdit,
  goToTargetDeliveryTemplateAccessFunctionView: actions.goToTargetDeliveryTemplateAccessFunctionView,
  selectTargetDeliveryTemplateAccessFunction: actions.selectTargetDeliveryTemplateAccessFunction,
  getTargetDeliveryTemplateAccessFunctionList: actions.getTargetDeliveryTemplateAccessFunctionList,
  deleteTargetDeliveryTemplateAccessFunction: actions.deleteTargetDeliveryTemplateAccessFunction,
  getSupportedFeature: warrantActions.getWarrantFormConfig,
  getTemplates: warrantActions.getAllTemplates,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter

export const TargetDeliveryTemplateAccessFunctionList: FunctionComponent<Props> = ({
  history,
  accessFunctions,
  data,
  deliveryFunctionId,
  privileges,
  beforeLogout,
  enabledAccessFunctions,
  accessFunctionTemplates,
  selectedTargetDeliveryTemplate,
  isAuditEnabled,
  userId,
  userName,
  getAccessFunctions,
  goToTargetDeliveryTemplateAccessFunctionCreate,
  goToTargetDeliveryTemplateAccessFunctionEdit,
  goToTargetDeliveryTemplateAccessFunctionView,
  selectTargetDeliveryTemplateAccessFunction,
  getTargetDeliveryTemplateAccessFunctionList,
  deleteTargetDeliveryTemplateAccessFunction,
  getSupportedFeature,
  getTemplates,
}: Props) => {

  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>();
  const [columnDefs] = useState<ColDef[]>(targetDeliveryTemplateAccessFunctionColumnDefs);
  const [
    accessFunctionGridRowData,
    setAccessFunctionGridRowData,
  ] = useState<TargetDeliveryTemplateAccessFunctionRowData[]>([]);

  useEffect(() => {
    getTargetDeliveryTemplateAccessFunctionList(deliveryFunctionId);
    getAccessFunctions(deliveryFunctionId);
    getSupportedFeature(deliveryFunctionId);
    getTemplates();
    return () => {
      AppToaster.clear();
    };
  }, [
    deliveryFunctionId,
    getTargetDeliveryTemplateAccessFunctionList,
    getAccessFunctions,
    getSupportedFeature,
    getTemplates,
  ]);

  useEffect(() => {
    setLastUpdatedAt(new Date());
  }, [data]);

  useEffect(() => {
    setLastUpdatedAt(new Date());

    const gridRowData = adaptAccessFunctionsGridRowData(
      data,
      accessFunctions,
      accessFunctionTemplates,
    );

    setAccessFunctionGridRowData(gridRowData);
  }, [
    data,
    accessFunctions,
    enabledAccessFunctions,
    accessFunctionTemplates,
  ]);

  const handleRefresh = () => getTargetDeliveryTemplateAccessFunctionList(deliveryFunctionId);

  const handleCreateNew = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    goToTargetDeliveryTemplateAccessFunctionCreate(history);
  };

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    const selectedRows = event.api.getSelectedRows().map((row) => {
      const { afTypeDisplay, ...rest } = row;
      return rest;
    })
    selectTargetDeliveryTemplateAccessFunction(selectedRows);
  };

  const handleGoToView = () => goToTargetDeliveryTemplateAccessFunctionView(history);

  const handleGoToEdit = () => goToTargetDeliveryTemplateAccessFunctionEdit(history);

  const handleDeleteTargetTemplate = () => {
    const targetDeliveryTemplate = selectedTargetDeliveryTemplate[0];
    if (targetDeliveryTemplate?.id == null) {
      return;
    }

    const body: WithAuditDetails<void> = { data: undefined };

    if (isAuditEnabled) {
      const auditDetails = getAuditDetails(
        isAuditEnabled,
        userId,
        userName,
        getAuditFieldInfo(null),
        targetDeliveryTemplate.name,
        AuditService.TARGET_TEMPLATE_PER_ACCESS_FUNCTION,
        AuditActionType.TARGET_TEMPLATE_PER_ACCESS_FUNCTION_DELETE,
      );
      body.auditDetails = auditDetails as AuditDetails;
    }

    deleteTargetDeliveryTemplateAccessFunction(
      deliveryFunctionId,
      targetDeliveryTemplate.id,
      body,
    );
  };

  const hasPrivilegeToView = privileges.includes(TargetDeliveryTemplateAccessFunctionPrivileges.View);
  const hasPrivilegesToCreate = privileges.includes(TargetDeliveryTemplateAccessFunctionPrivileges.Edit);
  const hasPrivilegeToDelete = privileges.includes(TargetDeliveryTemplateAccessFunctionPrivileges.Delete);

  return (
    <div data-testid="TargetDeliveryTemplateAccessFunctionList" style={{ height: 'inherit' }}>
      <TargetDeliveryTemplateAccessFunctionGrid
        data={accessFunctionGridRowData}
        selectedTargetDeliveryTemplate={selectedTargetDeliveryTemplate[0]}
        hasPrivilegeToView={hasPrivilegeToView}
        hasPrivilegesToCreate={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        noResults={false}
        beforeLogout={beforeLogout}
        lastUpdatedAt={lastUpdatedAt}
        columnDefs={columnDefs}
        onRefresh={handleRefresh}
        onCreateNew={handleCreateNew}
        onSelectionChanged={handleSelectionChanged}
        onDelete={handleDeleteTargetTemplate}
        onEdit={handleGoToEdit}
        onView={handleGoToView}
      />
    </div>
  );
};

export default withRouter(connector(TargetDeliveryTemplateAccessFunctionList));
